import { get, post, put } from '@/lib/api';
import { bool, rec } from './normalize';

/**
 * Stripe Connect: the firm's own account, and how it wants clients to pay it.
 *
 * Two booleans in this file are both called "ACH" by the server and mean
 * different things. They are named apart here and never merged:
 *
 *   `achCapability`   — Stripe has activated `us_bank_account_ach_payments`
 *                       on the connected account. The firm cannot change it;
 *                       until it is true a bank debit will not go through.
 *   `settings.achEnabled` — the firm has switched ACH on for its pay pages.
 *                       Intent only, storable before the capability lands.
 *
 * The status endpoint answers 200 with `enabled: false` when the Connect flag
 * is off on the server, so the UI hides itself without special-casing a 404.
 */

export type FeeMode = 'absorb' | 'pass_on';

export interface PaymentSettings {
  cardEnabled: boolean;
  achEnabled: boolean;
  cardFeeMode: FeeMode;
  achFeeMode: FeeMode;
  /** Absorb a passed-on fee the first time a client pays online. */
  waiveFirstPaymentFee: boolean;
  /** Show the setup banner in the app shell while payments are not live yet. */
  showSetupBanner: boolean;
  /** Email the client a receipt when they pay online. */
  sendClientReceipts: boolean;
  /** Copy the firm (company email + invoice CC list) on those receipts. */
  ccFirmOnReceipts: boolean;
}

export interface ConnectStatus {
  /** Connect is switched on for this server at all. */
  enabled: boolean;
  /** The team has a connected account. */
  connected: boolean;
  /** Stripe will take card payments on it. False while onboarding is unfinished. */
  chargesEnabled: boolean;
  /**
   * Stripe has everything it asked for on the account. Goes true before
   * `chargesEnabled` while Stripe verifies, and it is what gates the Express
   * dashboard link — Stripe refuses one until details are submitted.
   */
  detailsSubmitted: boolean;
  /** Stripe activated the bank-debit capability on the account. NOT the firm's setting. */
  achCapability: boolean;
  settings: PaymentSettings;
}

const DEFAULT_SETTINGS: PaymentSettings = {
  cardEnabled: true,
  achEnabled: false,
  cardFeeMode: 'absorb',
  achFeeMode: 'absorb',
  waiveFirstPaymentFee: false,
  showSetupBanner: true,
  sendClientReceipts: true,
  ccFirmOnReceipts: true
};

function toFeeMode(v: unknown, fallback: FeeMode): FeeMode {
  return v === 'absorb' || v === 'pass_on' ? v : fallback;
}

export function toPaymentSettings(v: unknown): PaymentSettings {
  const s = rec(v);
  return {
    cardEnabled: bool(s.cardEnabled, DEFAULT_SETTINGS.cardEnabled),
    achEnabled: bool(s.achEnabled, DEFAULT_SETTINGS.achEnabled),
    cardFeeMode: toFeeMode(s.cardFeeMode, DEFAULT_SETTINGS.cardFeeMode),
    achFeeMode: toFeeMode(s.achFeeMode, DEFAULT_SETTINGS.achFeeMode),
    waiveFirstPaymentFee: bool(s.waiveFirstPaymentFee, DEFAULT_SETTINGS.waiveFirstPaymentFee),
    showSetupBanner: bool(s.showSetupBanner, DEFAULT_SETTINGS.showSetupBanner),
    sendClientReceipts: bool(s.sendClientReceipts, DEFAULT_SETTINGS.sendClientReceipts),
    ccFirmOnReceipts: bool(s.ccFirmOnReceipts, DEFAULT_SETTINGS.ccFirmOnReceipts)
  };
}

/**
 * One cache entry for the Connect status, shared by every screen that asks.
 * The request costs a Stripe account lookup server-side, and the invoice screen
 * and the settings card both need the answer — separate keys would mean two
 * lookups and a settings change that never reaches the invoice menu.
 */
export const CONNECT_STATUS_KEY = ['connect', 'status'] as const;

export async function fetchConnectStatus(signal?: AbortSignal): Promise<ConnectStatus> {
  const raw = rec(await get<unknown>('/api/stripe/connect/status', undefined, signal));
  return {
    enabled: bool(raw.enabled),
    connected: bool(raw.connected),
    chargesEnabled: bool(raw.chargesEnabled),
    detailsSubmitted: bool(raw.detailsSubmitted),
    // Top-level `achEnabled` on the wire is the Stripe capability; the firm's
    // own switch is the one nested under `settings`.
    achCapability: bool(raw.achEnabled),
    settings: toPaymentSettings(raw.settings)
  };
}

/** Returns the URL of a Stripe-hosted onboarding session to redirect to. */
export const startConnectOnboarding = (opts: { returnTo?: 'onboarding' } = {}) =>
  post<{ url: string }>('/api/stripe/connect/onboard', opts);

/**
 * Returns a one-time URL into the firm's own Stripe Express dashboard — its
 * payouts, balance and payment history. Single-use and short-lived, so it is
 * fetched on the click rather than rendered into an href ahead of time.
 */
export const openConnectDashboard = () =>
  post<{ url: string }>('/api/stripe/connect/dashboard');

/** Partial patch — an absent key keeps whatever the server already holds. */
export async function savePaymentSettings(
  input: Partial<PaymentSettings>
): Promise<PaymentSettings> {
  const raw = rec(await put<unknown>('/api/stripe/payment-settings', input));
  return toPaymentSettings(raw.settings);
}
